import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { Button } from '../../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Textarea } from '../../components/ui/textarea';
import { toast } from 'sonner';
import { 
  Car, User, CheckCircle2, XCircle, Loader2, Phone, 
  Package, Wrench, IndianRupee, Image as ImageIcon, 
  Play, FileText, Clock, AlertCircle, ChevronDown, ChevronUp,
  Sun, Moon
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const CustomerJobView = () => {
  const { jobId } = useParams();
  const [job, setJob] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [itemDecisions, setItemDecisions] = useState({});
  const [expandedItems, setExpandedItems] = useState({});
  const [submitted, setSubmitted] = useState(false);
  const [isDarkMode, setIsDarkMode] = useState(() => {
    // Check localStorage for saved preference, default to true (dark mode)
    const saved = localStorage.getItem('propitstop_theme');
    return saved ? saved === 'dark' : true;
  });

  useEffect(() => {
    fetchJobDetails();
  }, [jobId]);

  useEffect(() => {
    // Save theme preference to localStorage
    localStorage.setItem('propitstop_theme', isDarkMode ? 'dark' : 'light');
  }, [isDarkMode]);

  const toggleTheme = () => {
    setIsDarkMode(!isDarkMode);
  };

  const fetchJobDetails = async () => {
    try {
      const response = await fetch(`${API_URL}/api/customer/job/${jobId}`);
      const data = await response.json();
      
      if (data.success) {
        setJob(data.job);
        // Initialize decisions based on existing statuses
        const decisions = {};
        data.job.estimate?.line_items?.forEach(item => {
          if (item.status === 'approved') {
            decisions[item.id] = { approved: true, note: '' };
          } else if (item.status === 'rejected') {
            decisions[item.id] = { approved: false, note: item.customer_note || '' };
          }
        });
        setItemDecisions(decisions);
        
        // Check if already submitted
        if (data.job.estimate?.customer_response_at) {
          setSubmitted(true);
        }
      } else {
        toast.error('Job not found');
      }
    } catch (error) {
      toast.error('Failed to load job details');
    } finally {
      setLoading(false);
    }
  };

  const toggleItemExpanded = (itemId) => {
    setExpandedItems(prev => ({
      ...prev,
      [itemId]: !prev[itemId]
    }));
  };

  const handleItemDecision = (itemId, approved) => {
    setItemDecisions(prev => ({
      ...prev,
      [itemId]: { ...prev[itemId], approved, note: prev[itemId]?.note || '' }
    }));
  };

  const handleItemNote = (itemId, note) => {
    setItemDecisions(prev => ({
      ...prev,
      [itemId]: { ...prev[itemId], note }
    }));
  };

  const handleSubmitDecisions = async () => {
    // Check if all items have decisions
    const pendingItems = job.estimate?.line_items?.filter(
      item => itemDecisions[item.id]?.approved === undefined
    );
    
    if (pendingItems?.length > 0) {
      toast.error(`Please approve or reject all items (${pendingItems.length} remaining)`);
      return;
    }

    setSubmitting(true);

    try {
      const items = Object.entries(itemDecisions).map(([id, decision]) => ({
        id,
        approved: decision.approved,
        note: decision.note || ''
      }));

      const response = await fetch(`${API_URL}/api/customer/job/${jobId}/approve`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ items })
      });

      const data = await response.json();
      
      if (data.success) {
        toast.success(`Response submitted! Approved: ${data.approved_count}, Rejected: ${data.rejected_count}`);
        setSubmitted(true);
        fetchJobDetails();
      } else {
        toast.error(data.detail || 'Failed to submit response');
      }
    } catch (error) {
      toast.error('Connection error');
    } finally {
      setSubmitting(false);
    }
  };

  const getStatusColor = (status) => {
    const colors = {
      pending: isDarkMode ? 'bg-yellow-500' : 'bg-yellow-400',
      approved: isDarkMode ? 'bg-green-500' : 'bg-green-500',
      rejected: isDarkMode ? 'bg-red-500' : 'bg-red-500'
    };
    return colors[status] || (isDarkMode ? 'bg-slate-500' : 'bg-slate-400');
  };

  const calculateApprovedTotal = () => {
    if (!job?.estimate?.line_items) return 0;
    
    let total = 0;
    job.estimate.line_items.forEach(item => {
      const decision = itemDecisions[item.id];
      if (decision?.approved) {
        // total_amount already includes GST (computed at estimate creation time)
        total += item.total_amount || 0;
      }
    });
    return total;
  };

  // Theme classes
  const theme = {
    // Backgrounds
    pageBg: isDarkMode 
      ? 'bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900' 
      : 'bg-gradient-to-br from-gray-50 via-white to-gray-100',
    cardBg: isDarkMode ? 'bg-slate-800/50 border-slate-700' : 'bg-white border-gray-200 shadow-sm',
    headerBg: 'bg-gradient-to-r from-orange-500 to-red-600',
    
    // Text colors
    textPrimary: isDarkMode ? 'text-white' : 'text-gray-900',
    textSecondary: isDarkMode ? 'text-slate-400' : 'text-gray-600',
    textMuted: isDarkMode ? 'text-slate-500' : 'text-gray-400',
    
    // Borders
    border: isDarkMode ? 'border-slate-700' : 'border-gray-200',
    borderLight: isDarkMode ? 'border-slate-600' : 'border-gray-300',
    
    // Input/Interactive
    inputBg: isDarkMode ? 'bg-slate-700/50 border-slate-600 text-white' : 'bg-gray-50 border-gray-300 text-gray-900',
    
    // Cards with status
    cardPending: isDarkMode ? 'bg-slate-800/50' : 'bg-white',
    cardApproved: isDarkMode ? 'bg-green-500/5' : 'bg-green-50',
    cardRejected: isDarkMode ? 'bg-red-500/5' : 'bg-red-50',
    
    // Special sections
    infoBg: isDarkMode ? 'bg-purple-500/10 border-purple-500/30' : 'bg-purple-50 border-purple-200',
    successBg: isDarkMode ? 'bg-green-500/10 border-green-500/30' : 'bg-green-50 border-green-200',
    warningBg: isDarkMode ? 'bg-yellow-500/10 border-yellow-500/30' : 'bg-yellow-50 border-yellow-200',
    
    // Footer
    footerBg: isDarkMode ? 'bg-slate-900/95 border-slate-700' : 'bg-white/95 border-gray-200 shadow-lg',
    
    // Buttons
    btnOutlineGreen: isDarkMode 
      ? 'border-green-500/50 text-green-400 hover:bg-green-500/10' 
      : 'border-green-500 text-green-600 hover:bg-green-50',
    btnOutlineRed: isDarkMode 
      ? 'border-red-500/50 text-red-400 hover:bg-red-500/10' 
      : 'border-red-500 text-red-600 hover:bg-red-50',
  };

  if (loading) {
    return (
      <div className={`min-h-screen ${theme.pageBg} flex items-center justify-center`}>
        <Loader2 className="w-10 h-10 animate-spin text-orange-500" />
      </div>
    );
  }

  if (!job) {
    return (
      <div className={`min-h-screen ${theme.pageBg} flex items-center justify-center`}>
        <Card className={`${theme.cardBg} p-8 text-center`}>
          <AlertCircle className="w-16 h-16 mx-auto mb-4 text-red-500" />
          <h2 className={`text-xl font-bold ${theme.textPrimary} mb-2`}>Job Not Found</h2>
          <p className={theme.textSecondary}>The job you're looking for doesn't exist or has been removed.</p>
        </Card>
      </div>
    );
  }

  const isPendingApproval = job.status === 'quote_raised' || job.status === 'estimate_sent';
  const alreadyResponded = job.estimate?.customer_response_at;

  return (
    <div className={`min-h-screen ${theme.pageBg}`}>
      {/* Header */}
      <header className={`${theme.headerBg} px-4 py-6`}>
        <div className="max-w-2xl mx-auto">
          <div className="flex items-center justify-between mb-4">
            <div>
              <p className="text-orange-100 text-sm">Job #{job.job_number}</p>
              <h1 className="text-xl font-bold text-white">{job.garage?.name}</h1>
            </div>
            <div className="flex items-center gap-2">
              {/* Theme Toggle Button */}
              <button
                onClick={toggleTheme}
                className="p-2 rounded-full bg-white/20 hover:bg-white/30 transition-colors"
                title={isDarkMode ? 'Switch to Light Mode' : 'Switch to Dark Mode'}
                data-testid="theme-toggle"
              >
                {isDarkMode ? (
                  <Sun className="w-5 h-5 text-white" />
                ) : (
                  <Moon className="w-5 h-5 text-white" />
                )}
              </button>
              {job.garage?.logo_url && (
                <img 
                  src={`${API_URL}${job.garage.logo_url}`} 
                  alt="Garage Logo" 
                  className="h-12 rounded-lg"
                />
              )}
            </div>
          </div>
          
          {/* Vehicle Info */}
          <div className="flex items-center gap-3 text-white">
            <Car className="w-5 h-5" />
            <span className="font-medium">
              {job.vehicle?.make} {job.vehicle?.model} ({job.vehicle?.number || 'No plate'})
            </span>
          </div>
        </div>
      </header>

      <main className="max-w-2xl mx-auto px-4 py-6 space-y-4 pb-32">
        {/* Status Banner */}
        {alreadyResponded && (
          <Card className={theme.successBg}>
            <CardContent className="p-4 flex items-center gap-3">
              <CheckCircle2 className="w-6 h-6 text-green-500" />
              <div>
                <p className="text-green-600 font-medium">Response Submitted</p>
                <p className={`text-sm ${theme.textSecondary}`}>
                  You have already submitted your response on {new Date(job.estimate.customer_response_at).toLocaleDateString()}
                </p>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Customer Complaint */}
        {job.complaint && (
          <Card className={theme.cardBg}>
            <CardHeader className="py-3">
              <CardTitle className={`${theme.textPrimary} text-sm flex items-center gap-2`}>
                <FileText className="w-4 h-4 text-orange-400" /> Your Complaint
              </CardTitle>
            </CardHeader>
            <CardContent className="pt-0">
              <p className={theme.textSecondary}>{job.complaint}</p>
            </CardContent>
          </Card>
        )}

        {/* Vehicle Images */}
        {job.images && job.images.length > 0 && (
          <Card className={theme.cardBg}>
            <CardHeader className="py-3">
              <CardTitle className={`${theme.textPrimary} text-sm flex items-center gap-2`}>
                <ImageIcon className="w-4 h-4 text-blue-400" /> Vehicle Images
              </CardTitle>
            </CardHeader>
            <CardContent className="pt-0">
              <div className="grid grid-cols-3 gap-2">
                {job.images.slice(0, 6).map((img, idx) => (
                  <div key={idx} className={`aspect-square rounded-lg overflow-hidden border ${theme.borderLight}`}>
                    <img src={img.url} alt={img.type} className="w-full h-full object-cover" />
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Estimate Section */}
        {job.estimate && (
          <>
            <Card className={theme.cardBg}>
              <CardHeader className="py-3">
                <CardTitle className={`${theme.textPrimary} text-sm flex items-center gap-2`}>
                  <Package className="w-4 h-4 text-green-400" /> Service Estimate
                </CardTitle>
                {job.estimate.notes && (
                  <p className={`text-sm ${theme.textSecondary} mt-1`}>{job.estimate.notes}</p>
                )}
              </CardHeader>
            </Card>

            {/* Line Items */}
            <div className="space-y-3">
              {job.estimate.line_items?.map((item, index) => {
                const decision = itemDecisions[item.id];
                const isExpanded = expandedItems[item.id];
                const hasProof = item.proof_images?.length > 0 || item.proof_video_url;
                
                const cardStyle = decision?.approved === true 
                  ? `border-l-green-500 ${theme.cardApproved}` 
                  : decision?.approved === false 
                  ? `border-l-red-500 ${theme.cardRejected}` 
                  : item.type === 'part' 
                  ? `border-l-blue-500 ${theme.cardPending}` 
                  : `border-l-purple-500 ${theme.cardPending}`;
                
                return (
                  <Card 
                    key={item.id} 
                    className={`border-l-4 ${cardStyle} ${theme.border}`}
                  >
                    <CardContent className="p-4">
                      {/* Item Header */}
                      <div className="flex items-start justify-between mb-3">
                        <div className="flex-1">
                          <div className="flex items-center gap-2 mb-1">
                            {item.type === 'part' ? (
                              <Package className="w-4 h-4 text-blue-400" />
                            ) : (
                              <Wrench className="w-4 h-4 text-purple-400" />
                            )}
                            <span className={`text-xs ${theme.textMuted} uppercase`}>{item.type}</span>
                            <Badge className={`text-xs ${getStatusColor(item.status)}`}>
                              {item.status}
                            </Badge>
                          </div>
                          <h3 className={`${theme.textPrimary} font-medium`}>
                            {item.part_name || item.description}
                          </h3>
                          {item.part_name && item.description && (
                            <p className={`text-sm ${theme.textSecondary} mt-1`}>{item.description}</p>
                          )}
                        </div>
                        <div className="text-right">
                          <p className={`${theme.textPrimary} font-bold`}>₹{item.total_amount?.toLocaleString()}</p>
                          <p className={`text-xs ${theme.textMuted}`}>
                            {item.quantity} × ₹{item.unit_price?.toLocaleString()}
                          </p>
                          {item.type === 'labour' && (
                            <p className="text-xs text-orange-500">+18% GST</p>
                          )}
                        </div>
                      </div>

                      {/* AI Explanation */}
                      {item.ai_explanation && (
                        <div className={`${theme.infoBg} border rounded-lg p-3 mb-3`}>
                          <p className={`text-sm ${isDarkMode ? 'text-purple-300' : 'text-purple-700'}`}>
                            <span className="font-medium">💡 Why needed:</span> {item.ai_explanation}
                          </p>
                        </div>
                      )}

                      {/* Proof Section */}
                      {hasProof && (
                        <div className="mb-3">
                          <button
                            onClick={() => toggleItemExpanded(item.id)}
                            className="flex items-center gap-2 text-sm text-blue-500 hover:text-blue-400"
                          >
                            <ImageIcon className="w-4 h-4" />
                            View Proof ({item.proof_images?.length || 0} images)
                            {isExpanded ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                          </button>
                          
                          {isExpanded && (
                            <div className="mt-3 space-y-3">
                              {/* Images */}
                              {item.proof_images?.length > 0 && (
                                <div className="grid grid-cols-2 gap-2">
                                  {item.proof_images.map((img, imgIdx) => (
                                    <a 
                                      key={imgIdx} 
                                      href={img} 
                                      target="_blank" 
                                      rel="noopener noreferrer"
                                      className={`block aspect-video rounded-lg overflow-hidden border ${theme.borderLight} hover:border-blue-500 transition-colors`}
                                    >
                                      <img src={img} alt="Proof" className="w-full h-full object-cover" />
                                    </a>
                                  ))}
                                </div>
                              )}
                              
                              {/* Video */}
                              {item.proof_video_url && (
                                <a 
                                  href={item.proof_video_url}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className={`flex items-center gap-2 p-3 ${isDarkMode ? 'bg-slate-700/50' : 'bg-gray-100'} rounded-lg text-blue-500 hover:text-blue-400`}
                                >
                                  <Play className="w-5 h-5" />
                                  <span>Watch Video Proof</span>
                                </a>
                              )}
                            </div>
                          )}
                        </div>
                      )}

                      {/* Decision Buttons - Only show if pending approval */}
                      {isPendingApproval && !alreadyResponded && (
                        <div className="space-y-3">
                          <div className="flex gap-2">
                            <Button
                              onClick={() => handleItemDecision(item.id, true)}
                              variant={decision?.approved === true ? "default" : "outline"}
                              className={`flex-1 ${
                                decision?.approved === true 
                                  ? 'bg-green-600 hover:bg-green-700 text-white' 
                                  : theme.btnOutlineGreen
                              }`}
                              data-testid={`approve-item-${item.id}`}
                            >
                              <CheckCircle2 className="w-4 h-4 mr-2" />
                              Approve
                            </Button>
                            <Button
                              onClick={() => handleItemDecision(item.id, false)}
                              variant={decision?.approved === false ? "default" : "outline"}
                              className={`flex-1 ${
                                decision?.approved === false 
                                  ? 'bg-red-600 hover:bg-red-700 text-white' 
                                  : theme.btnOutlineRed
                              }`}
                              data-testid={`reject-item-${item.id}`}
                            >
                              <XCircle className="w-4 h-4 mr-2" />
                              Reject
                            </Button>
                          </div>
                          
                          {/* Note field when rejected */}
                          {decision?.approved === false && (
                            <Textarea
                              placeholder="Reason for rejection (optional)"
                              value={decision.note || ''}
                              onChange={(e) => handleItemNote(item.id, e.target.value)}
                              className={theme.inputBg}
                              rows={2}
                            />
                          )}
                        </div>
                      )}
                    </CardContent>
                  </Card>
                );
              })}
            </div>

            {/* Totals Card */}
            <Card className={`${isDarkMode ? 'bg-gradient-to-r from-orange-500/10 to-red-500/10 border-orange-500/30' : 'bg-gradient-to-r from-orange-50 to-red-50 border-orange-200'}`}>
              <CardContent className="p-4 space-y-2">
                <div className="flex justify-between text-sm">
                  <span className={theme.textSecondary}>Parts Subtotal</span>
                  <span className={theme.textPrimary}>₹{job.estimate.subtotal_parts?.toLocaleString()}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className={theme.textSecondary}>Labour Subtotal</span>
                  <span className={theme.textPrimary}>₹{job.estimate.subtotal_labour?.toLocaleString()}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className={theme.textSecondary}>GST (CGST + SGST)</span>
                  <span className={theme.textPrimary}>₹{job.estimate.total_gst?.toLocaleString()}</span>
                </div>
                <div className={`flex justify-between text-xl font-bold border-t ${isDarkMode ? 'border-orange-500/30' : 'border-orange-200'} pt-2`}>
                  <span className={theme.textPrimary}>Estimate Total</span>
                  <span className="text-orange-500">₹{job.estimate.grand_total?.toLocaleString()}</span>
                </div>
                
                {/* Show approved total if decisions made */}
                {Object.keys(itemDecisions).length > 0 && (
                  <div className={`flex justify-between text-lg font-medium border-t ${isDarkMode ? 'border-green-500/30' : 'border-green-200'} pt-2 mt-2`}>
                    <span className="text-green-500">Your Approved Total</span>
                    <span className="text-green-500">₹{calculateApprovedTotal().toLocaleString()}</span>
                  </div>
                )}
              </CardContent>
            </Card>
          </>
        )}

        {/* QC Proofs Section */}
        {job.qc_proofs && job.qc_proofs.length > 0 && (
          <Card className={theme.cardBg}>
            <CardHeader className="py-3">
              <CardTitle className={`${theme.textPrimary} text-sm flex items-center gap-2`}>
                <CheckCircle2 className="w-4 h-4 text-green-400" /> Quality Check Proofs
              </CardTitle>
            </CardHeader>
            <CardContent className="pt-0 space-y-3">
              {job.qc_proofs.map((proof, idx) => (
                <div key={idx} className={`${isDarkMode ? 'bg-slate-700/30' : 'bg-gray-50'} rounded-lg p-3`}>
                  <p className={`${theme.textPrimary} font-medium mb-2`}>{proof.description}</p>
                  {proof.images?.length > 0 && (
                    <div className="grid grid-cols-3 gap-2">
                      {proof.images.map((img, imgIdx) => (
                        <a 
                          key={imgIdx} 
                          href={img}
                          target="_blank"
                          rel="noopener noreferrer"
                          className={`aspect-square rounded-lg overflow-hidden border ${theme.borderLight}`}
                        >
                          <img src={img} alt="QC Proof" className="w-full h-full object-cover" />
                        </a>
                      ))}
                    </div>
                  )}
                  <Badge className={`mt-2 ${proof.status === 'passed' ? 'bg-green-500' : 'bg-yellow-500'}`}>
                    {proof.status}
                  </Badge>
                </div>
              ))}
            </CardContent>
          </Card>
        )}

        {/* Invoice Section */}
        {job.invoice && (
          <Card className={theme.successBg}>
            <CardHeader className="py-3">
              <CardTitle className={`${theme.textPrimary} text-sm flex items-center gap-2`}>
                <FileText className="w-4 h-4 text-green-400" /> Invoice Generated
              </CardTitle>
            </CardHeader>
            <CardContent className="pt-0">
              <div className="flex justify-between items-center">
                <div>
                  <p className={`${theme.textPrimary} font-medium`}>Invoice #{job.invoice.invoice_number}</p>
                  <p className="text-green-500 text-lg font-bold">₹{job.invoice.grand_total?.toLocaleString()}</p>
                </div>
                {job.invoice.is_paid ? (
                  <Badge className="bg-green-600">Paid</Badge>
                ) : (
                  <Badge className="bg-yellow-500">Payment Pending</Badge>
                )}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Contact Section */}
        <Card className={theme.cardBg}>
          <CardContent className="p-4">
            <p className={`${theme.textSecondary} text-sm mb-2`}>Questions? Contact the garage:</p>
            <div className="flex items-center gap-3">
              <a 
                href={`tel:${job.garage?.mobile}`}
                className="flex-1 flex items-center justify-center gap-2 bg-green-600 hover:bg-green-700 text-white py-3 rounded-lg font-medium transition-colors"
              >
                <Phone className="w-5 h-5" />
                Call {job.garage?.name}
              </a>
            </div>
          </CardContent>
        </Card>
      </main>

      {/* Fixed Bottom Submit Bar - Only show if pending approval */}
      {isPendingApproval && !alreadyResponded && job.estimate && (
        <div className={`fixed bottom-0 left-0 right-0 ${theme.footerBg} border-t p-4 backdrop-blur-sm`}>
          <div className="max-w-2xl mx-auto">
            <div className="flex items-center justify-between mb-2">
              <span className={`${theme.textSecondary} text-sm`}>
                {Object.keys(itemDecisions).length} of {job.estimate.line_items?.length} items decided
              </span>
              <span className="text-green-500 font-medium">
                Approved: ₹{calculateApprovedTotal().toLocaleString()}
              </span>
            </div>
            <Button
              onClick={handleSubmitDecisions}
              disabled={submitting || Object.keys(itemDecisions).length !== job.estimate.line_items?.length}
              className="w-full bg-gradient-to-r from-orange-500 to-red-600 hover:from-orange-600 hover:to-red-700 text-lg py-6"
              data-testid="submit-decisions-btn"
            >
              {submitting ? (
                <Loader2 className="w-5 h-5 mr-2 animate-spin" />
              ) : (
                <CheckCircle2 className="w-5 h-5 mr-2" />
              )}
              Submit Response
            </Button>
          </div>
        </div>
      )}

      {/* Footer */}
      <div className={`text-center py-4 ${theme.textMuted} text-xs`}>
        Powered by Propitstop
      </div>
    </div>
  );
};

export default CustomerJobView;
